import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Shield, FileText, Euro, Building2, Scale, CheckCircle, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";

const GarantiesMarchesPublicsArticle = () => {
  return (
    <>
      <Helmet>
        <title>Guide des Garanties dans les Marchés Publics 2025 | Mon DCE Express</title>
        <meta name="description" content="Tout savoir sur les garanties dans les marchés publics : retenue de garantie, caution bancaire, garantie à première demande, garantie décennale. Guide complet 2025." />
        <meta name="keywords" content="garantie marché public, retenue de garantie, caution bancaire, garantie première demande, garantie décennale, assurance marché public" />
        <link rel="canonical" href="https://www.dce-express.fr/blog/garanties-marches-publics" />
      </Helmet>

      <article className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/blog" className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Guide Complet
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Réglementation
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Guide Complet des Garanties dans les Marchés Publics
            </h1>
            
            <p className="text-xl text-blue-100 mb-6 leading-relaxed">
              Retenue de garantie, caution bancaire, garantie à première demande, garantie décennale... 
              Tout comprendre sur les garanties exigées dans les marchés publics.
            </p>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-blue-100">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Mis à jour 2025
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                15 min de lecture
              </span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Les garanties constituent un élément essentiel des marchés publics. Elles protègent 
              l'acheteur public contre les risques d'inexécution ou de mauvaise exécution du marché. 
              Ce guide détaille toutes les garanties que vous pouvez rencontrer.
            </p>

            {/* Sommaire */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 my-8">
              <h3 className="text-lg font-bold text-blue-800 mb-4">Sommaire</h3>
              <ol className="space-y-2 text-blue-700">
                <li>1. La retenue de garantie</li>
                <li>2. La garantie à première demande</li>
                <li>3. La caution personnelle et solidaire</li>
                <li>4. La garantie de bonne exécution</li>
                <li>5. La garantie décennale</li>
                <li>6. L'assurance responsabilité civile</li>
                <li>7. Tableau comparatif</li>
              </ol>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Euro className="w-7 h-7 text-blue-600" />
              1. La retenue de garantie
            </h2>

            <p>
              La retenue de garantie est la forme de garantie la plus courante dans les marchés publics. 
              Elle consiste en un prélèvement effectué par l'acheteur sur chaque paiement d'acompte.
            </p>

            <div className="bg-slate-100 rounded-xl p-6 my-6">
              <h4 className="font-semibold text-slate-800 mb-4">Caractéristiques principales :</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Taux maximum :</strong> 5% du montant initial du marché (TTC)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Durée :</strong> Jusqu'à l'expiration du délai de garantie (1 an après réception)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Libération :</strong> Automatique à défaut de réserves, sur demande du titulaire</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 my-6">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Attention
              </h4>
              <p className="text-amber-900">
                La retenue de garantie peut être remplacée par une garantie à première demande 
                ou une caution personnelle et solidaire. C'est souvent plus avantageux pour 
                la trésorerie de l'entreprise.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Shield className="w-7 h-7 text-indigo-600" />
              2. La garantie à première demande
            </h2>

            <p>
              C'est l'engagement pris par un établissement financier (banque, assureur) de verser 
              au bénéficiaire une somme déterminée, dès la première demande de ce dernier, 
              sans pouvoir opposer aucune exception.
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Avantages pour l'acheteur
                </h4>
                <ul className="space-y-2 text-green-900 text-sm">
                  <li>• Paiement immédiat sur simple demande</li>
                  <li>• Sécurité maximale</li>
                  <li>• Pas besoin de prouver la faute</li>
                </ul>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Avantages pour l'entreprise
                </h4>
                <ul className="space-y-2 text-blue-900 text-sm">
                  <li>• Pas d'impact sur la trésorerie</li>
                  <li>• Remplace la retenue de garantie</li>
                  <li>• Coût généralement modéré</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Building2 className="w-7 h-7 text-purple-600" />
              3. La caution personnelle et solidaire
            </h2>

            <p>
              C'est l'engagement d'un tiers (généralement une banque ou un assureur) de payer 
              les sommes dues par le titulaire défaillant. Contrairement à la garantie à première 
              demande, la caution peut opposer des exceptions.
            </p>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 my-6">
              <h4 className="font-semibold text-purple-800 mb-3">Conditions d'utilisation :</h4>
              <ul className="space-y-2 text-purple-900">
                <li>• Le garant doit être un établissement financier agréé</li>
                <li>• L'engagement doit être solidaire (pas de bénéfice de discussion)</li>
                <li>• La caution doit couvrir l'intégralité de la garantie demandée</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <FileText className="w-7 h-7 text-emerald-600" />
              4. La garantie de bonne exécution
            </h2>

            <p>
              Elle vise à garantir la bonne exécution du marché pendant toute sa durée. 
              Elle peut prendre la forme d'une retenue de garantie majorée ou d'une garantie bancaire.
            </p>

            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 my-6">
              <h4 className="font-semibold text-emerald-800 mb-3">Quand est-elle exigée ?</h4>
              <ul className="space-y-2 text-emerald-900">
                <li>• Marchés de grande importance</li>
                <li>• Prestations complexes ou risquées</li>
                <li>• Marchés avec avance importante</li>
                <li>• Fournisseurs présentant un risque particulier</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Scale className="w-7 h-7 text-orange-600" />
              5. La garantie décennale
            </h2>

            <p>
              Obligatoire pour les marchés de travaux, la garantie décennale couvre les dommages 
              qui compromettent la solidité de l'ouvrage ou le rendent impropre à sa destination 
              pendant 10 ans après la réception.
            </p>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 my-6">
              <h4 className="font-semibold text-orange-800 mb-3">Dommages couverts :</h4>
              <ul className="space-y-2 text-orange-900">
                <li>• Effondrement ou menace d'effondrement</li>
                <li>• Fissures importantes affectant la structure</li>
                <li>• Infiltrations rendant l'ouvrage inhabitable</li>
                <li>• Défauts d'étanchéité majeurs</li>
                <li>• Problèmes de fondations</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Shield className="w-7 h-7 text-cyan-600" />
              6. L'assurance responsabilité civile professionnelle
            </h2>

            <p>
              Elle couvre les dommages causés aux tiers dans le cadre de l'exécution du marché. 
              C'est une obligation pour toute entreprise exerçant une activité professionnelle.
            </p>

            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-6 my-6">
              <h4 className="font-semibold text-cyan-800 mb-3">Ce qui est couvert :</h4>
              <div className="grid md:grid-cols-2 gap-4 text-cyan-900">
                <ul className="space-y-1">
                  <li>✓ Dommages corporels</li>
                  <li>✓ Dommages matériels</li>
                  <li>✓ Dommages immatériels</li>
                </ul>
                <ul className="space-y-1">
                  <li>✓ Frais de défense</li>
                  <li>✓ Préjudices financiers</li>
                  <li>✓ Atteinte à l'environnement</li>
                </ul>
              </div>
            </div>

            {/* Tableau comparatif */}
            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              7. Tableau comparatif des garanties
            </h2>

            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-4 text-left">Garantie</th>
                    <th className="p-4 text-center">Taux/Montant</th>
                    <th className="p-4 text-center">Durée</th>
                    <th className="p-4 text-center">Obligatoire</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b bg-white">
                    <td className="p-4 font-medium">Retenue de garantie</td>
                    <td className="p-4 text-center">5% max</td>
                    <td className="p-4 text-center">Délai de garantie</td>
                    <td className="p-4 text-center">Non</td>
                  </tr>
                  <tr className="border-b bg-slate-50">
                    <td className="p-4 font-medium">Garantie à 1ère demande</td>
                    <td className="p-4 text-center">Variable</td>
                    <td className="p-4 text-center">Selon marché</td>
                    <td className="p-4 text-center">Non</td>
                  </tr>
                  <tr className="border-b bg-white">
                    <td className="p-4 font-medium">Garantie décennale</td>
                    <td className="p-4 text-center">Valeur ouvrage</td>
                    <td className="p-4 text-center">10 ans</td>
                    <td className="p-4 text-center text-green-600 font-bold">Oui (travaux)</td>
                  </tr>
                  <tr className="border-b bg-slate-50">
                    <td className="p-4 font-medium">RC Professionnelle</td>
                    <td className="p-4 text-center">Variable</td>
                    <td className="p-4 text-center">Durée activité</td>
                    <td className="p-4 text-center text-green-600 font-bold">Oui</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Conclusion */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-2xl p-8 my-12">
              <h3 className="text-xl font-bold mb-4">À retenir</h3>
              <p className="text-slate-300 leading-relaxed">
                Les garanties sont un élément clé des marchés publics. Bien les comprendre vous permet 
                de mieux préparer vos offres et d'optimiser votre trésorerie. Privilégiez les garanties 
                bancaires plutôt que les retenues de garantie lorsque c'est possible.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-center text-white mt-12">
            <h3 className="text-2xl font-bold mb-4">
              Besoin d'aide pour vos candidatures ?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Mon DCE Express analyse vos DCE et identifie les garanties demandées 
              pour vous aider à préparer vos offres.
            </p>
            <Link to="/analyse-gratuite">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
                Analyser un DCE gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default GarantiesMarchesPublicsArticle;
